/**
 * AccessRoleLocationSelector
 *
 * Reusable Access Type Tier / Organization / Department / Role / Location block
 * (same APIs and scope rules as the Submit Ticket access-request form).
 * Uncontrolled: seed with `initial*` props and read changes via `onChange`.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Platform, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { createApiClient } from "../../../api/apiClient";
import useSupportTierRoles from "../../Admin/pages/UserManagement/hooks/useSupportTierRoles";
import SelectField from "./SelectField";
import AccessRequestLocationPicker, { LocationPickerType, LocationRegion } from "./AccessRequestLocationPicker";
import {
  ACCESS_TYPE_TIERS,
  AR_DEPARTMENTS,
  AR_DEPARTMENTS_BY_ORG,
  AR_ORGANIZATIONS,
  ApiRoleOption,
  SUPPORT_ROLE_NAMES,
  extractRoleOptionsFromResponse,
  getMaxRegionSelectionsForRole,
  getRoleScopeConfig,
} from "./SubmitTicketFormPanel";

type AccessTier = (typeof ACCESS_TYPE_TIERS)[number];

export type AccessLocationPayload = {
  locationType: "LOCATION" | "MARKET" | "REGION";
  locationId: string;
  locationName: string;
  region?: string;
  market?: string;
  site?: string;
};

export type AccessRoleLocationValue = {
  /** "Normal User" for Field/3PL tiers, otherwise the support role name */
  accessType: string;
  accessTier: AccessTier;
  organization: string;
  department: string;
  role: string;
  roleId: number | null;
  selectedLocationNames: string[];
  selectedLocations: AccessLocationPayload[];
};

type Props = {
  initialRole?: string;
  initialOrganization?: string;
  initialDepartment?: string;
  initialLocations?: string[];
  roleLabel?: string;
  onChange: (value: AccessRoleLocationValue) => void;
};

const normalize = (v: string) => v.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
const isSupportRole = (name?: string) =>
  !!name && (SUPPORT_ROLE_NAMES as readonly string[]).includes(name);
const rowsFrom = (body: any): any[] =>
  Array.isArray(body) ? body : body?.content ?? body?.data?.content ?? body?.data ?? [];

const AccessRoleLocationSelector: React.FC<Props> = ({
  initialRole = "",
  initialOrganization = "",
  initialDepartment = "",
  initialLocations = [],
  roleLabel = "Role",
  onChange,
}) => {
  const [tier, setTier] = useState<AccessTier>(isSupportRole(initialRole) ? "Support" : "Field");
  const [organization, setOrganization] = useState(initialOrganization);
  const [department, setDepartment] = useState(initialDepartment);
  const [role, setRole] = useState(initialRole);
  const [selected, setSelected] = useState<string[]>(initialLocations);
  const [roles, setRoles] = useState<ApiRoleOption[]>([]);
  const [tree, setTree] = useState<LocationRegion[]>([]);
  const [idMap, setIdMap] = useState<Record<string, string>>({});
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { supportTierRoles, supportTierRolesLoading } = useSupportTierRoles("access-request");

  const departmentOptions = useMemo(
    () => (organization ? AR_DEPARTMENTS_BY_ORG[organization] ?? [...AR_DEPARTMENTS] : [...AR_DEPARTMENTS]),
    [organization]
  );

  useEffect(() => {
    if (!department) {
      setRoles([]);
      return;
    }
    let cancelled = false;
    createApiClient()
      .get("/admin/users/roles", { params: { department } })
      .then((res) => { if (!cancelled) setRoles(extractRoleOptionsFromResponse(res.data)); })
      .catch(() => { if (!cancelled) setRoles([]); });
    return () => { cancelled = true; };
  }, [department]);

  useEffect(() => {
    if (!organization || !role) {
      setTree([]);
      setIdMap({});
      return;
    }
    let cancelled = false;
    setLocationsLoading(true);
    createApiClient()
      .get("/admin/locations", { params: { organizationType: organization, page: 0, pageSize: 2000 } })
      .then((res) => {
        if (cancelled) return;
        const rows = rowsFrom(res.data);
        const regionMap = new Map<string, Map<string, Map<string, string>>>();
        const ids: Record<string, string> = {};
        for (const loc of rows) {
          const reg = loc.region ?? "Unknown";
          const mkt = loc.market ?? "Unknown";
          const siteName = String(loc.locName ?? "").trim();
          const key = String(loc.locId ?? loc.id ?? "").trim() || siteName;
          const dbId = String(loc.id ?? "").trim();
          if (siteName && dbId) ids[siteName] = dbId;
          if (!key) continue;
          if (!regionMap.has(reg)) regionMap.set(reg, new Map());
          const mktMap = regionMap.get(reg)!;
          if (!mktMap.has(mkt)) mktMap.set(mkt, new Map());
          mktMap.get(mkt)!.set(key, siteName || key);
        }
        setIdMap(ids);
        setTree(
          Array.from(regionMap.entries()).map(([region, mktMap]) => ({
            region,
            siteCount: Array.from(mktMap.values()).reduce((s, sites) => s + sites.size, 0),
            markets: Array.from(mktMap.entries()).map(([market, sites]) => ({
              market,
              siteCount: sites.size,
              sites: Array.from(sites.values()).map((name) => ({ name })),
            })),
          }))
        );
      })
      .catch(() => { if (!cancelled) { setTree([]); setIdMap({}); } })
      .finally(() => { if (!cancelled) setLocationsLoading(false); });
    return () => { cancelled = true; };
  }, [organization, role]);

  const roleOptions = tier === "Support" ? supportTierRoles.map((r) => r.roleName) : department ? roles.map((r) => r.name) : [];
  const roleScope = role ? getRoleScopeConfig(role, roles) : null;
  const scopeType = roleScope?.type;
  const pickerType: LocationPickerType = scopeType === "region" ? "region" : scopeType === "market" ? "market" : "site";
  const showPicker = !!roleScope && scopeType !== "full";

  const roleId = useMemo(() => {
    if (!role) return null;
    const target = normalize(role);
    const fieldMatch = roles.find((r) => normalize(r.name) === target);
    const support = supportTierRoles.find((r) => normalize(r.roleName) === target);
    const id = Number(fieldMatch?.id ?? support?.roleId);
    return Number.isFinite(id) ? id : null;
  }, [role, roles, supportTierRoles]);

  const selectedLocations = useMemo<AccessLocationPayload[]>(() => {
    if (!showPicker) return [];
    return selected.map((sel): AccessLocationPayload => {
      if (pickerType === "region") {
        return { locationType: "REGION", locationId: sel, locationName: sel, region: sel };
      }
      if (pickerType === "market") {
        const reg = tree.find((r) => r.markets.some((m) => m.market === sel));
        return { locationType: "MARKET", locationId: sel, locationName: sel, region: reg?.region, market: sel };
      }
      const reg = tree.find((r) => r.markets.some((m) => m.sites.some((s) => s.name === sel)));
      const mkt = reg?.markets.find((m) => m.sites.some((s) => s.name === sel));
      return {
        locationType: "LOCATION",
        locationId: idMap[sel] ?? sel,
        locationName: sel,
        region: reg?.region,
        market: mkt?.market,
        site: sel,
      };
    });
  }, [selected, showPicker, pickerType, tree, idMap]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current({
      accessType: tier === "Support" ? role : "Normal User",
      accessTier: tier,
      organization,
      department,
      role,
      roleId,
      selectedLocationNames: showPicker ? selected : [],
      selectedLocations,
    });
  }, [tier, organization, department, role, roleId, selected, showPicker, selectedLocations]);

  const resetRoleAndLocations = () => {
    setRole("");
    setSelected([]);
  };
  const zStyle = (z: number) =>
    Platform.select({ web: { overflow: "visible", zIndex: z, position: "relative" } as any, default: {} });

  return (
    <View style={s.wrap}>
      <View style={[s.field, zStyle(50)]}>
        <SelectField
          id="ar-sel-tier"
          label="Access Type Tier"
          value={tier}
          options={[...ACCESS_TYPE_TIERS]}
          required
          activeDropdownId={openDropdown}
          onDropdownToggle={setOpenDropdown}
          onChange={(v) => {
            setTier(v as AccessTier);
            resetRoleAndLocations();
            if (v === "Support") {
              setOrganization("");
              setDepartment("");
            }
          }}
        />
      </View>

      {tier !== "Support" ? (
        <View style={[s.row, zStyle(40)]}>
          <SelectField
            id="ar-sel-org"
            label="Organization"
            value={organization}
            options={[...AR_ORGANIZATIONS]}
            required
            placeholder="Select organization..."
            activeDropdownId={openDropdown}
            onDropdownToggle={setOpenDropdown}
            onChange={(v) => {
              setOrganization(v);
              setDepartment("");
              resetRoleAndLocations();
            }}
          />
          <SelectField
            id="ar-sel-dept"
            label="Department"
            value={department}
            options={departmentOptions}
            required
            placeholder={organization ? "Select a department..." : "Select an organization first"}
            disabled={!organization}
            activeDropdownId={openDropdown}
            onDropdownToggle={setOpenDropdown}
            onChange={(v) => {
              setDepartment(v);
              resetRoleAndLocations();
            }}
          />
        </View>
      ) : null}

      <View style={zStyle(30)}>
        <SelectField
          id="ar-sel-role"
          label={roleLabel}
          value={role}
          options={roleOptions}
          required
          placeholder={
            tier === "Support"
              ? supportTierRolesLoading ? "Loading roles..." : "Select role..."
              : department ? "Select a role..." : "Select a department first"
          }
          disabled={tier === "Support" ? false : !department}
          activeDropdownId={openDropdown}
          onDropdownToggle={setOpenDropdown}
          onChange={(v) => {
            setRole(v);
            setSelected([]);
          }}
        />
        {roleScope ? (
          <Text style={s.helper}>
            Role scope:{" "}
            <Text style={{ color: roleScope.accessColor, fontWeight: "700" }}>{roleScope.accessLabel}</Text>
            {" — "}
            {roleScope.scopeLabel}
          </Text>
        ) : null}
      </View>

      {scopeType === "full" ? (
        <View style={s.fullBanner}>
          <MaterialIcons name="shield" size={16} color="#15803d" />
          <Text style={s.fullBannerText}>
            <Text style={{ fontWeight: "700" }}>{role}</Text> grants access to all locations - no site selection needed.
          </Text>
        </View>
      ) : null}

      {showPicker ? (
        <View style={{ marginTop: 12 }}>
          {locationsLoading ? (
            <Text style={[s.helper, { color: "#005cbc", textAlign: "center", paddingVertical: 12 }]}>
              Loading locations…
            </Text>
          ) : (
            <AccessRequestLocationPicker
              pickerType={pickerType}
              organization={organization}
              selectedItems={selected}
              onSelectionChange={setSelected}
              locationTree={tree}
              maxRegionSelections={getMaxRegionSelectionsForRole(role)}
            />
          )}
        </View>
      ) : null}
    </View>
  );
};

const s = StyleSheet.create({
  wrap: { width: "100%" },
  field: { marginBottom: 4 },
  row: { flexDirection: "row", flexWrap: "wrap", columnGap: 24, marginTop: 4 },
  helper: { fontSize: 12, color: "#6b7280", marginTop: 4 },
  fullBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#bbf7d0",
    backgroundColor: "#f0fdf4",
  },
  fullBannerText: { flex: 1, fontSize: 13, color: "#166534", lineHeight: 19 },
});

export default AccessRoleLocationSelector;
